import classNames from "classnames";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import {
  SupportSignature,
  useDeleteSupportSignatureMutation,
  useGetSupportSignaturesQuery,
  useSaveSupportSignatureMutation,
} from "../../slices/SupportSlice";
import {
  errorText,
  NO_SIGNATURE,
  SIGNATURE_MAX,
  signatureParts,
  signatureProblem,
} from "./support.utils";
import { PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";

const CARD = "rounded-[14px] border border-[#EDEFF3] bg-white";
const INPUT =
  "h-9 w-full rounded-lg border border-[#DCE2EA] px-3 text-[13.5px] text-[#1F2433] outline-none focus:border-[#6B4EFF]";
/** Same width as in the email (server SignatureLogoWidth). */
const LOGO_WIDTH = 120; // = the server's SignatureLogoWidth (owner 2026-10-30: 20% smaller)

/** The signature as the customer sees it under the email: the text, then ProCharted's logo. */
export const SignaturePreview = ({
  body,
  logoUrl,
  className,
}: {
  body: string;
  logoUrl: string;
  className?: string;
}) => (
  <div
    className={classNames(
      "text-[14px] leading-[1.5] text-[#1F2433]",
      className,
    )}
    style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
    data-testid={"signature-preview"}>
    {body.trim() && (
      <p className={"mb-[3px]"}>
        {body.split("\n").map((line, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {signatureParts(line).map((part, j) =>
              part.url ? (
                <a
                  key={j}
                  href={part.url}
                  target={"_blank"}
                  rel={"noreferrer"}
                  className={"text-[#6B4EFF] underline"}>
                  {part.text}
                </a>
              ) : (
                <React.Fragment key={j}>{part.text}</React.Fragment>
              ),
            )}
          </React.Fragment>
        ))}
      </p>
    )}
    <img
      src={logoUrl}
      alt={"ProCharted"}
      width={LOGO_WIDTH}
      style={{ width: LOGO_WIDTH, height: "auto", display: "block" }}
    />
  </div>
);

/**
 * Help desk settings › Email signatures (owner 2026-10-27): like Outlook's saved signatures, shared by everyone who
 * uses the Help desk. Several allowed, one default (picked under every reply). The logo is always ProCharted's own.
 */
export const SignaturesCard = () => {
  const { data, isLoading, isError } = useGetSupportSignaturesQuery();
  const [save, { isLoading: isSaving }] = useSaveSupportSignatureMutation();
  const [remove, { isLoading: isRemoving }] =
    useDeleteSupportSignatureMutation();
  const [editing, setEditing] = useState<{
    id?: string;
    name: string;
    body: string;
    isDefault: boolean;
  } | null>(null);
  const [asking, setAsking] = useState<string | null>(null);

  const startNew = () =>
    setEditing({
      name: "",
      body: "",
      isDefault: !data?.signatures.length,
    });
  const startEdit = (s: SupportSignature) =>
    setEditing({
      id: s.id,
      name: s.name,
      body: s.body,
      isDefault: s.isDefault,
    });

  const problem = editing ? signatureProblem(editing.name, editing.body) : null;

  const doSave = async () => {
    if (!editing || problem) return;
    try {
      await save(editing).unwrap();
      toast("Signature saved.", { type: "success" });
      setEditing(null);
    } catch (e) {
      toast(errorText(e, "The signature wasn't saved."), { type: "error" });
    }
  };
  const makeDefault = (s: SupportSignature) =>
    save({ id: s.id, name: s.name, body: s.body, isDefault: true })
      .unwrap()
      .catch((e) =>
        toast(errorText(e, "Couldn't change the default."), { type: "error" }),
      );
  const doDelete = (id: string) =>
    remove(id)
      .unwrap()
      .then(() => setAsking(null))
      .catch((e) =>
        toast(errorText(e, "The signature wasn't deleted."), { type: "error" }),
      );

  return (
    <section
      className={classNames(CARD, "mb-5")}
      data-testid={"signatures-card"}>
      <div className={"border-b border-[#EDEFF3] px-[18px] py-4"}>
        <h2 className={"text-[17px] font-extrabold text-[#1F2433]"}>
          Email signatures
        </h2>
        <p className={"mt-1 text-[13.5px] leading-relaxed text-[#5D6475]"}>
          Added under every email the Help desk sends, like in Outlook. The
          default one is picked for you; you can choose another one (or none)
          before you click Send. The ProCharted logo is always added under the
          text.
        </p>
      </div>
      <div className={"px-[18px] py-4"}>
        {isLoading ? (
          <div className={"text-[13.5px] text-[#5D6475]"}>Loading…</div>
        ) : isError ? (
          <div className={"text-[13.5px] text-[#5D6475]"}>
            The signatures couldn&apos;t be loaded. Please refresh the page.
          </div>
        ) : !data?.available ? (
          <div className={"text-[13.5px] text-[#5D6475]"}>
            Signatures need a database update first.
          </div>
        ) : (
          <>
            {data.signatures.length === 0 && !editing && (
              <div className={"mb-3 text-[13.5px] text-[#5D6475]"}>
                No signatures yet. Emails go out without one.
              </div>
            )}
            <div className={"flex flex-col gap-3"}>
              {data.signatures.map((s) =>
                editing?.id === s.id ? null : (
                  <div
                    key={s.id}
                    className={
                      "flex flex-wrap items-start gap-4 rounded-xl border border-[#EDEFF3] px-4 py-3"
                    }
                    data-testid={"signature-row"}>
                    <div className={"min-w-[180px] flex-1"}>
                      <div className={"mb-2 flex items-center gap-2"}>
                        <b className={"text-[14px] text-[#1F2433]"}>{s.name}</b>
                        {s.isDefault && (
                          <span
                            className={
                              "rounded-full bg-[#EFEBFF] px-2 py-0.5 text-[11.5px] font-bold text-[#4B32D6]"
                            }>
                            Default
                          </span>
                        )}
                      </div>
                      <SignaturePreview body={s.body} logoUrl={data.logoUrl} />
                    </div>
                    <div className={"flex flex-wrap gap-2"}>
                      {asking === s.id ? (
                        <>
                          <button
                            type={"button"}
                            disabled={isRemoving}
                            className={classNames(
                              SMALL_BUTTON,
                              "text-[#B42318]",
                            )}
                            onClick={() => void doDelete(s.id)}>
                            Yes, delete
                          </button>
                          <button
                            type={"button"}
                            className={SMALL_BUTTON}
                            onClick={() => setAsking(null)}>
                            Keep
                          </button>
                        </>
                      ) : (
                        <>
                          {!s.isDefault && (
                            <button
                              type={"button"}
                              disabled={isSaving}
                              className={SMALL_BUTTON}
                              onClick={() => void makeDefault(s)}>
                              Make default
                            </button>
                          )}
                          <button
                            type={"button"}
                            className={SMALL_BUTTON}
                            onClick={() => startEdit(s)}>
                            Edit
                          </button>
                          <button
                            type={"button"}
                            className={classNames(
                              SMALL_BUTTON,
                              "text-[#B42318]",
                            )}
                            onClick={() => setAsking(s.id)}>
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ),
              )}
            </div>
            {editing ? (
              <div
                className={
                  "mt-3 rounded-xl border-[1.5px] border-[#6B4EFF] bg-[#FBFAFF] px-4 py-4"
                }
                data-testid={"signature-editor"}>
                <div className={"grid gap-4 md:grid-cols-2"}>
                  <div className={"flex flex-col gap-3"}>
                    <label
                      className={"text-[12.5px] font-semibold text-[#3A4256]"}>
                      Name (only you see it)
                      <input
                        id={"signature-name"}
                        className={classNames(INPUT, "mt-1")}
                        value={editing.name}
                        maxLength={60}
                        placeholder={"e.g. Standard"}
                        onChange={(e) =>
                          setEditing({ ...editing, name: e.target.value })
                        }
                      />
                    </label>
                    <label
                      className={"text-[12.5px] font-semibold text-[#3A4256]"}>
                      Signature text
                      <textarea
                        id={"signature-body"}
                        className={
                          "mt-1 w-full resize-y rounded-lg border border-[#DCE2EA] bg-white px-3 py-2 text-[13.5px] leading-relaxed text-[#1F2433] outline-none focus:border-[#6B4EFF]"
                        }
                        rows={6}
                        maxLength={SIGNATURE_MAX + 200}
                        placeholder={
                          "Ruddens Bosse\nFounder, ProCharted\nsupport@procharted.com\nhttps://procharted.com"
                        }
                        value={editing.body}
                        onChange={(e) =>
                          setEditing({ ...editing, body: e.target.value })
                        }
                      />
                    </label>
                    <label
                      className={
                        "flex items-center gap-2 text-[13.5px] text-[#1F2433]"
                      }>
                      <input
                        type={"checkbox"}
                        checked={editing.isDefault}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            isDefault: e.target.checked,
                          })
                        }
                        className={"h-4 w-4 accent-[#6B4EFF]"}
                      />
                      Use as the default signature
                    </label>
                  </div>
                  <div>
                    <div
                      className={
                        "mb-1 text-[12.5px] font-semibold text-[#3A4256]"
                      }>
                      Preview
                    </div>
                    <div
                      className={
                        "rounded-lg border border-[#EDEFF3] bg-white px-4 py-3"
                      }>
                      <p
                        className={"mb-3 text-[14px] text-[#9097A8]"}
                        style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
                        …your reply…
                      </p>
                      <SignaturePreview
                        body={editing.body}
                        logoUrl={data.logoUrl}
                      />
                    </div>
                    <p className={"mt-2 text-[12px] text-[#7A7F8C]"}>
                      Links starting with https:// become clickable. The logo
                      can&apos;t be changed.
                    </p>
                  </div>
                </div>
                {problem && (editing.name || editing.body) && (
                  <div className={"mt-3 text-[12.5px] text-[#9A5B00]"}>
                    {problem}
                  </div>
                )}
                <div className={"mt-4 flex gap-2"}>
                  <button
                    type={"button"}
                    disabled={!!problem || isSaving}
                    title={problem || undefined}
                    onClick={() => void doSave()}
                    className={PRIMARY_BUTTON}>
                    {isSaving ? "Saving…" : "Save signature"}
                  </button>
                  <button
                    type={"button"}
                    className={SMALL_BUTTON}
                    onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type={"button"}
                className={classNames(SMALL_BUTTON, "mt-3")}
                onClick={startNew}
                data-testid={"new-signature"}>
                + New signature
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
};

/**
 * Under the reply box: which signature goes under this email ("none" = no signature) and how it will look.
 * Hidden while the server has no signature table.
 */
export const SignaturePicker = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const { data } = useGetSupportSignaturesQuery();
  if (!data?.available) return null;
  const picked = data.signatures.find((s) => s.id === value);
  return (
    <div
      className={"mt-3 border-t border-dashed border-[#DCE2EA] pt-3"}
      data-testid={"signature-picker"}>
      <div className={"mb-2 flex flex-wrap items-center gap-2 text-[12.5px]"}>
        <label htmlFor={"reply-signature"} className={"text-[#7A7F8C]"}>
          Signature
        </label>
        {data.signatures.length > 0 ? (
          <select
            id={"reply-signature"}
            value={picked ? picked.id : NO_SIGNATURE}
            onChange={(e) => onChange(e.target.value)}
            className={
              "h-8 rounded-lg border border-[#DCE2EA] bg-white px-2 text-[13px] text-[#1F2433] outline-none focus:border-[#6B4EFF]"
            }>
            {data.signatures.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.isDefault ? " (default)" : ""}
              </option>
            ))}
            <option value={NO_SIGNATURE}>No signature</option>
          </select>
        ) : (
          <span className={"text-[#5D6475]"}>None yet</span>
        )}
        <Link
          to={"/support/settings"}
          className={"text-[#6B4EFF] hover:underline"}>
          {data.signatures.length > 0 ? "Edit signatures" : "Add a signature"}
        </Link>
      </div>
      {picked && (
        <SignaturePreview
          body={picked.body}
          logoUrl={data.logoUrl}
          className={"opacity-80"}
        />
      )}
    </div>
  );
};
